import Form from "@/components/Form";
import { QueryTable } from "@/components/query-table/QueryTable";
import SaveButton from "@/components/SaveButton";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { useEffect, useCallback } from "react";
import { InvoiceSpecificationStoresForm } from "@/schemas/invoiceSpecificationStoresForm";
import { useFormMergeWithDefaults } from "@/hooks/useFormMergeWithDefaults";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import * as Sentry from "@sentry/react";
import { flattenFormErrors } from "@/lib/utils/flattenFormErrors";

interface LaborCostsInStoresFormProps {
  onIncludeOnInvoiceChange?: (
    stores: InvoiceSpecificationStoresForm["stores"],
  ) => void;
  onEditLaborCostsInStores?: (
    values: InvoiceSpecificationStoresForm["stores"],
    dirtyFields: Record<string, unknown>,
  ) => Promise<void>;
  form: UseFormReturn<InvoiceSpecificationStoresForm>;
  defaultValues: InvoiceSpecificationStoresForm;
}

export const LaborCostsInStoresForm = ({
  onIncludeOnInvoiceChange,
  onEditLaborCostsInStores,
  form,
  defaultValues,
}: LaborCostsInStoresFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleValidationError = useCallback(
    (errors: FieldErrors<InvoiceSpecificationStoresForm>) => {
      const fieldErrors = flattenFormErrors(errors);
      Sentry.captureException(
        new Error("LaborCostsInStoresForm validation failed"),
        {
          extra: {
            fieldErrors,
            formValues: form.getValues(),
          },
        },
      );
      toast({
        title: t("laborCostsInStores.validation.title"),
        description: t("laborCostsInStores.validation.description"),
        variant: "destructive",
      });
    },
    [form, t, toast],
  );

  const handleFormSubmit = useCallback(
    async (values: InvoiceSpecificationStoresForm) => {
      if (onEditLaborCostsInStores) {
        await onEditLaborCostsInStores(
          values.stores,
          form.formState.dirtyFields.stores || {},
        );
      }
    },
    [onEditLaborCostsInStores, form.formState.dirtyFields],
  );

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name?.endsWith(".include_on_invoice") && values?.stores) {
        onIncludeOnInvoiceChange?.(
          values.stores as InvoiceSpecificationStoresForm["stores"],
        );
      }
    });

    return () => subscription.unsubscribe();
  }, [form, onIncludeOnInvoiceChange]);

  useFormMergeWithDefaults(form, defaultValues);

  return (
    <Form
      form={form}
      handleSubmit={handleFormSubmit}
      onValidationError={handleValidationError}
      className="h-full gap-1"
    >
      <QueryTable.Content>
        <QueryTable.Status>
          <QueryTable.DirtyRowsStatus />
        </QueryTable.Status>
      </QueryTable.Content>
      <footer className="flex p-2 justify-end gap-6">
        <SaveButton />
      </footer>
    </Form>
  );
};
